import i18n from 'i18next';
import { get, snakeCase } from 'lodash';

import {
  AssetAttributeSelectFilter,
  LastInspectedFilter,
  NextInspectionDueFilter,
  SimpleAssetFilter,
  SiteFilter,
} from 'components/DataTable/customFilters';
import {
  LAST_INSPECTED_FILTER_KEYS,
  LAST_INSPECTED_KEY,
  NOT_INSPECTED_SINCE_KEY,
} from 'components/DataTable/customFilters/LastInspectedFilter';
import {
  NEXT_INSPECTION_DUE_KEY,
  parseNextInspectionDueUrlFilter,
} from 'components/DataTable/customFilters/NextInspectionDueFilter';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAsset,
  type AtlasGqlAssetFieldDefinition,
  type AtlasGqlGetAssetFieldsForTypeQuery,
  type AtlasGqlGetAssetTypesAndFieldsQuery,
  type AtlasGqlSite,
} from 'types/atlas-graphql';
import { TABLE_FILTER_OPERATORS } from 'utils/constants';
import { resolveRelativeDateToken } from 'utils/relativeDateTokens';

import { getFilterDropdown, sortBy } from '../helpers';

import { generateDateColumn, generateNumberColumn, generateSelectColumn } from './columnHelpers';
import { type TBladeFields, type TTurbineFields } from './types';
import { generateColumnFieldOptions, getOptionsForField } from './utils';

export const allAssetsTypeName = 'All';
export const allAssetsMenuKey = 'all-assets';

type AssetTypeWithFields = AtlasGqlGetAssetTypesAndFieldsQuery['assetTypes'][number];

export type AssetTableColumnOptions = {
  /** Appends Last Inspected and Next Inspection Due (release toggle MISSED_INSPECTIONS). */
  showInspectionColumns?: boolean;
};

// Asset Column - The unique identifier for the asset
export const assetColumn: TTableColumnDef = {
  key: 'assetName',
  title: i18n.t('table.asset', 'Asset'),
  dataIndex: 'assetName',
  filterProperty: '',
  sorter: false,
};

// Asset Type
export const assetTypeColumn: TTableColumnDef = {
  key: 'assetTypeName',
  title: i18n.t('table.assetType', 'Asset Type'),
  dataIndex: 'assetTypeName',
  filterProperty: 'assetTypeName',
  filterKeyOverride: 'assetTypeNames',
  sorter: sortBy('assetTypeName'),
};

/** Asset type display name → internal name, as the Asset Type column's server filter sends it. */
export const getAssetTypeInternalName = (assetTypeName: string) => snakeCase(assetTypeName);

function assetTypeSelectColumn(assetTypeNames: string[]): TTableColumnDef {
  return {
    ...assetTypeColumn,
    filters: assetTypeNames.map(generateColumnFieldOptions),
    filterDropdown: getFilterDropdown({
      key: 'assetTypeName',
      type: 'string',
    }),
    filterMultiple: false,
    filterKeyOverride: 'assetTypeNames',
    serverSideFilterParsers: {
      assetTypeName: (value: unknown) => getAssetTypeInternalName(String(value)),
    },
  };
}

// NOTE: The intent is for this to be the generic asset status column for all assets
// TODO: It is currently only supported in the Blade table
// Component Status
export function assetStatusColumn(options: string[]): TTableColumnDef {
  return generateSelectColumn(
    'component_status',
    i18n.t('table.status', 'Status'),
    true,
    options.map(generateColumnFieldOptions)
  );
}

// Location
export const assetLocationColumn: TTableColumnDef = {
  key: 'assetLocation',
  title: i18n.t('table.location', 'Location'),
  dataIndex: 'assetLocation',
  filterProperty: '',
  sorter: false,
};

// Parent Unique Identifier
export const assetParentColumn: TTableColumnDef = {
  key: 'assetParentName',
  title: i18n.t('table.parentAsset', 'Parent Asset'),
  dataIndex: 'assetParentName',
  filterProperty: '',
  sorter: false,
};

// Parent Asset Type
export const assetParentTypeColumn: TTableColumnDef = {
  key: 'assetParentTypeName',
  title: i18n.t('table.parentAssetType', 'Parent Asset Type'),
  dataIndex: 'assetParentTypeName',
  filterProperty: '',
  sorter: false,
};

// Filterable site column
export const filterableSiteColumn: TTableColumnDef<AtlasGqlSite> = {
  className: 'wrap-column',
  key: 'site',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'site',
  filterKeyOverride: 'descendantOfId',
  filterDropdown: SiteFilter,
  sorter: true,
};

export const siteAddressColumn: TTableColumnDef = {
  className: 'wrap-column',
  key: 'address',
  title: i18n.t('table.address', 'Address'),
  dataIndex: 'address',
  filterProperty: 'address',
  sorter: sortBy('address'),
};

export const siteCityColumn: TTableColumnDef = {
  className: 'wrap-column',
  key: 'city',
  title: i18n.t('table.city', 'City'),
  dataIndex: 'city',
  filterProperty: 'city',
  sorter: sortBy('city'),
};

export const siteCountryColumn: TTableColumnDef = {
  className: 'wrap-column',
  key: 'country',
  title: i18n.t('table.country', 'Country'),
  dataIndex: 'country',
  filterProperty: 'country',
  sorter: sortBy('country'),
};

export const siteStateColumn: TTableColumnDef = {
  className: 'wrap-column',
  key: 'state',
  title: i18n.t('table.state', 'State'),
  dataIndex: 'state',
  filterProperty: 'state',
  sorter: sortBy('state'),
};

export const siteZipColumn: TTableColumnDef = {
  key: 'zip',
  title: i18n.t('table.zip', 'Zip'),
  dataIndex: 'zip',
  filterProperty: 'zip',
  sorter: sortBy('zip'),
};

export const siteTimeZoneColumn: TTableColumnDef = {
  key: 'timezone',
  title: i18n.t('table.timeZone', 'Time Zone'),
  dataIndex: 'timezone',
  filterProperty: 'timezone',
  sorter: sortBy('timezone'),
};

export const siteTurbinesCountColumn: TTableColumnDef = {
  key: 'turbineCount',
  title: i18n.t('table.numberOfTurbines', 'Number of Turbines'),
  dataIndex: 'turbineCount',
  sorter: sortBy('turbineCount'),
};

export const siteCapacityColumn: TTableColumnDef = {
  key: 'capacity',
  title: i18n.t('table.capacity', 'Capacity'),
  dataIndex: 'capacity',
  filterProperty: 'capacity',
  sorter: sortBy('capacity'),
};

export const siteAlternativeNamesColumn: TTableColumnDef = {
  className: 'wrap-column',
  key: 'alternativeNames',
  title: i18n.t('table.alternativeNames', 'Alternative Names'),
  dataIndex: 'Alternative Names',
  filterProperty: 'alternativeNames',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  sorter: sortBy('alternativeNames'),
};

// Inspection columns. Values come from the Asset's own released Inspections;
// nothing rolls up from child Assets.
export const lastInspectedColumn: TTableColumnDef = {
  key: LAST_INSPECTED_KEY,
  title: i18n.t('table.lastInspected', 'Last Inspected'),
  dataIndex: 'lastInspected',
  // Saved views store the filter's sub-keys, so every one of them must resolve to this column.
  allKeys: LAST_INSPECTED_FILTER_KEYS,
  filterDropdown: props => <LastInspectedFilter {...props} />,
  serverSideFilterParsers: {
    [NOT_INSPECTED_SINCE_KEY]: (value: unknown) => resolveRelativeDateToken(value, 'past'),
  },
  sorter: true,
};

export const nextInspectionDueColumn: TTableColumnDef = {
  key: NEXT_INSPECTION_DUE_KEY,
  title: i18n.t('table.nextInspectionDue', 'Next Inspection Due'),
  dataIndex: 'nextInspectionDue',
  filterDropdown: props => <NextInspectionDueFilter {...props} />,
  serverSideFilterParsers: {
    [NEXT_INSPECTION_DUE_KEY]: (value: unknown) => resolveRelativeDateToken(value, 'future'),
  },
  parseUrlFilter: parseNextInspectionDueUrlFilter,
  sorter: true,
};

function withInspectionColumns(
  columns: TTableColumnDef[],
  { showInspectionColumns = false }: AssetTableColumnOptions
): TTableColumnDef[] {
  return showInspectionColumns
    ? [...columns, lastInspectedColumn, nextInspectionDueColumn]
    : columns;
}

// Turbine-Specific Column Definitions
export const turbineColumn: TTableColumnDef<AtlasGqlAsset> = {
  key: 'assetIds',
  title: i18n.t('table.turbine', 'Turbine'),
  dataIndex: 'Name',
  filterDropdown: props => <SimpleAssetFilter assetTypeName={'Turbine'} {...props} />,
  sorter: true,
  sortKey: 'name',
};

export function getTurbineColumnForSite(siteAssetId: string): TTableColumnDef<AtlasGqlAsset> {
  return {
    key: 'assetIds',
    title: i18n.t('table.turbine', 'Turbine'),
    dataIndex: 'Name',
    filterDropdown: props => (
      <SimpleAssetFilter
        assetTypeName={'Turbine'}
        defaultSelectedSiteIds={[siteAssetId]}
        {...props}
      />
    ),
    sorter: true,
    sortKey: 'name',
  };
}

export const turbineSiteColumn: TTableColumnDef = {
  key: 'ancestorSiteName',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'turbine_site',
  filterProperty: '',
  sorter: true,
};

export function turbineStatusColumn(options: string[]): TTableColumnDef {
  return generateSelectColumn('status', 'Status', true, options.map(generateColumnFieldOptions));
}

export const turbineMakeColumn: TTableColumnDef = {
  key: 'turbine_make',
  title: i18n.t('table.make', 'Make'),
  dataIndex: 'Make',
  filterDropdown: props => <AssetAttributeSelectFilter fieldName="turbine_make" {...props} />,
  sorter: sortBy('make'),
  get: (record: AtlasGqlAsset) => get(record, 'make'),
};

export const turbineModelColumn: TTableColumnDef = {
  key: 'turbine_model',
  title: i18n.t('table.model', 'Model'),
  dataIndex: 'Model',
  filterDropdown: props => (
    <AssetAttributeSelectFilter
      fieldName="turbine_model"
      independentFieldFilterKey="make"
      {...props}
    />
  ),
  sorter: sortBy('model'),
  get: (record: AtlasGqlAsset) => get(record, 'model'),
};

export const turbineSerialNumberColumn: TTableColumnDef = {
  key: 'serial_number',
  title: i18n.t('table.serialNumber', 'Serial Number'),
  dataIndex: 'Serial Number',
  filterProperty: 'serial_number',
  sorter: sortBy('serial_number'),
  get: (record: AtlasGqlAsset) => get(record, 'serial_number'),
};

export const turbineAlternativeNamesColumn: TTableColumnDef = {
  className: 'wrap-column',
  key: 'alternative_names',
  title: i18n.t('table.alternativeNames', 'Alternative Names'),
  dataIndex: 'Alternative Names',
  filterProperty: 'alternative_names',
  defaultFilterOperator: TABLE_FILTER_OPERATORS.CONTAINS,
  sorter: sortBy('Alternative Names'),
};

export const turbineLatitudeColumn: TTableColumnDef = generateNumberColumn(
  'latitude',
  i18n.t('table.latitude', 'Latitude'),
  true,
  [
    {
      label: -90,
    },
    { label: 90 },
  ]
);

export const turbineLongitudeColumn: TTableColumnDef = generateNumberColumn(
  'longitude',
  i18n.t('table.longitude', 'Longitude'),
  true,
  [
    {
      label: -180,
    },
    { label: 180 },
  ]
);

export const turbineHubHeightColumn: TTableColumnDef = generateNumberColumn(
  'hub_height',
  i18n.t('table.hubHeight', 'Hub Height'),
  true,
  [
    {
      label: 0,
    },
    { label: 1000 },
  ]
);

export const turbineYearOnlineColumn: TTableColumnDef = generateNumberColumn(
  'year_online',
  'Year Online',
  true,
  [
    {
      label: 1950,
    },
    {
      // Always set max as the current year
      label: new Date().getFullYear(),
    },
  ],
  1
);

export const turbineCODDateColumn: TTableColumnDef = generateDateColumn(
  'cod_date',
  'COD Date',
  'COD Date',
  true
);

export const turbineEOWDateColumn: TTableColumnDef = generateDateColumn(
  'eow_date',
  'EOW Date',
  'EOW Date',
  true
);

export const turbineCapacityColumn: TTableColumnDef = generateNumberColumn(
  'capacity',
  'Capacity',
  true,
  [
    {
      label: 0,
    },
    { label: 1000 },
  ]
);

// Blade-specific columns
export const bladeNameColumn: TTableColumnDef = {
  key: 'serial_number',
  title: i18n.t('table.serialNo', 'Serial No.'),
  dataIndex: 'Name', /// Does not map to FieldDefintion name. Will be populated manually.
  filterProperty: 'serial_number',
  sorter: sortBy('serial_number'),
};

export const bladeTurbineColumn: TTableColumnDef = {
  key: 'ancestorTurbineName',
  title: i18n.t('table.turbine', 'Turbine'),
  dataIndex: 'blade_turbine',
  filterProperty: '',
  sorter: true,
};

export const bladeSiteColumn: TTableColumnDef = {
  key: 'ancestorSiteName',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'blade_site',
  filterProperty: '',
  sorter: true,
};

export function bladePositionColumn(options: string[]): TTableColumnDef {
  return generateSelectColumn(
    'blade_position',
    'Position',
    true,
    options.map(generateColumnFieldOptions)
  );
}

export const bladeMakeColumn: TTableColumnDef = {
  key: 'blade_make',
  title: i18n.t('table.make', 'Make'),
  dataIndex: 'Make',
  filterDropdown: props => <AssetAttributeSelectFilter fieldName="blade_make" {...props} />,
  sorter: sortBy('make'),
  get: (record: AtlasGqlAsset) => get(record, 'make'),
};

export const bladeModelColumn: TTableColumnDef = {
  key: 'blade_model',
  title: i18n.t('table.model', 'Model'),
  dataIndex: 'Model',
  filterDropdown: props => (
    <AssetAttributeSelectFilter
      fieldName="blade_model"
      independentFieldFilterKey="make"
      {...props}
    />
  ),
  sorter: sortBy('model'),
  get: (record: AtlasGqlAsset) => get(record, 'model'),
};

export function bladeSerialNumberVerifiedColumn(options: string[]): TTableColumnDef {
  return generateSelectColumn(
    'serial_number_verified',
    'Serial Number Verified',
    true,
    options.map(generateColumnFieldOptions)
  );
}

export const bladeLengthColumn: TTableColumnDef = generateNumberColumn('length', 'Length', true, [
  // FIXME JD: Update this to query asset service for the range?
  {
    label: 0,
  },
  { label: 150 },
]);

export const bladeInstallDateColumn: TTableColumnDef = generateDateColumn(
  'install_date',
  'Install Date',
  'Install Date',
  true
);

export const bladeRemovedDateColumn: TTableColumnDef = generateDateColumn(
  'removed_date',
  'Removed Date',
  'Removed Date',
  true
);

export const bladeAuxiliaryComponentsColumn: TTableColumnDef = {
  key: 'auxiliary_component',
  title: i18n.t('table.auxiliaryComponent', 'Auxiliary Component'),
  dataIndex: 'Aux Component', // Does not map to FieldDefintion name. Will be populated manually.
  filterProperty: '',
  sorter: false,
};

// Generic Asset Type Columns
export const componentTurbineColumn: TTableColumnDef<AtlasGqlAsset> = {
  key: 'asset_turbine',
  title: i18n.t('table.turbine', 'Turbine'),
  dataIndex: 'asset_turbine',
  filterKeyOverride: 'descendantOfId',
  filterDropdown: props => <SimpleAssetFilter assetTypeName={'Turbine'} {...props} />,
  sorter: true,
  sortKey: 'ancestorTurbineName',
};

export const assetTurbineColumn: TTableColumnDef = {
  key: 'ancestorTurbineName',
  title: i18n.t('table.turbine', 'Turbine'),
  dataIndex: 'asset_turbine',
  filterProperty: '',
  sorter: true,
};

export const assetSiteColumn: TTableColumnDef = {
  key: 'ancestorSiteName',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'asset_site',
  filterProperty: '',
  sorter: true,
};

function getSiteColumns() {
  return [
    {
      ...filterableSiteColumn,
      filterKeyOverride: 'asset_id',
      dataIndex: 'name',
      sortKey: 'name',
    },
    siteAlternativeNamesColumn,
    siteTurbinesCountColumn,
    siteCapacityColumn,
    siteCountryColumn,
    siteAddressColumn,
    siteCityColumn,
    siteStateColumn,
    siteZipColumn,
    siteTimeZoneColumn,
  ];
}

export function getTurbineColumns(fields: TTurbineFields, parentSiteAssetId?: string) {
  return [
    ...(parentSiteAssetId
      ? [
          getTurbineColumnForSite(parentSiteAssetId),
          turbineAlternativeNamesColumn,
          turbineSiteColumn,
        ]
      : [
          turbineColumn,
          turbineAlternativeNamesColumn,
          {
            ...filterableSiteColumn,
            dataIndex: 'turbine_site',
            sortKey: 'ancestorSiteName',
          },
        ]),
    turbineStatusColumn(fields.statusOptions),
    turbineMakeColumn,
    turbineModelColumn,
    turbineSerialNumberColumn,
    turbineLatitudeColumn,
    turbineLongitudeColumn,
    turbineHubHeightColumn,
    turbineYearOnlineColumn,
    turbineCODDateColumn,
    turbineEOWDateColumn,
    turbineCapacityColumn,
  ];
}

export function getBladeColumns(fields: TBladeFields) {
  return [
    bladeNameColumn,
    {
      ...filterableSiteColumn,
      dataIndex: 'blade_site',
      sortKey: 'ancestorSiteName',
    },
    {
      ...componentTurbineColumn,
      dataIndex: 'blade_turbine',
    },
    assetStatusColumn(fields.assetStatusOptions),
    bladePositionColumn(fields.bladePositionOptions),
    bladeMakeColumn,
    bladeModelColumn,
    bladeSerialNumberVerifiedColumn(fields.serialNumberVerifiedOptions),
    bladeLengthColumn,
    bladeInstallDateColumn,
    bladeRemovedDateColumn,
    bladeAuxiliaryComponentsColumn,
  ];
}

export function generateUniqueIdentifierColumnForAsset(
  assetTypeData: AtlasGqlGetAssetFieldsForTypeQuery
): TTableColumnDef {
  const uniqueField = assetTypeData?.assetType?.fields?.find(
    field => field.isUniqueIdentifierField
  );

  if (uniqueField) {
    const name = uniqueField.name;
    return {
      key: uniqueField.searchKey,
      title: name,
      dataIndex: name,
      filterProperty: uniqueField.searchKey,
      sorter: sortBy(name),
    };
  }
  // Default column, we should never get here.
  return {
    key: 'key',
    title: 'title',
    dataIndex: 'title',
    filterProperty: '',
  };
}

function generateColumnForAssetField(
  field: Pick<AtlasGqlAssetFieldDefinition, 'name' | 'searchKey'>,
  assetTypeName?: string,
  enableServerSortAndFilter: boolean = true
): TTableColumnDef {
  const name = field.name;
  return {
    key: field.searchKey,
    title: name,
    dataIndex: name,
    ...(enableServerSortAndFilter ? { filterProperty: field.searchKey } : {}),
    ...(enableServerSortAndFilter && name === 'Make'
      ? {
          filterDropdown: props => (
            <AssetAttributeSelectFilter fieldName="make" assetTypeName={assetTypeName} {...props} />
          ),
        }
      : {}),
    ...(enableServerSortAndFilter && name === 'Model'
      ? {
          filterDropdown: props => (
            <AssetAttributeSelectFilter
              fieldName="model"
              independentFieldFilterKey="make"
              assetTypeName={assetTypeName}
              {...props}
            />
          ),
        }
      : {}),
    sorter: enableServerSortAndFilter ? sortBy(name) : false,
  };
}

// Function that can be used to dynamically generate columns based on the field definitions of an asset type.
// Ignore the unique identifer field, as that is generated elsewhere.
export function generateColumnsForAssetFields(
  assetTypeData: AtlasGqlGetAssetFieldsForTypeQuery
): TTableColumnDef[] {
  const fields =
    assetTypeData?.assetType?.fields?.filter(field => !field.isUniqueIdentifierField) || [];

  return fields.map(field => generateColumnForAssetField(field, assetTypeData?.assetType?.name));
}

export function generateColumnsForAllAssetTypeFields(
  assetTypes: AssetTypeWithFields[]
): TTableColumnDef[] {
  const seenFieldNames = new Set<string>();
  const columns: TTableColumnDef[] = [];

  const sortedAssetTypes = [...assetTypes].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const pinnedFieldNames = ['Name'];

  pinnedFieldNames.forEach(pinnedName => {
    for (const assetType of sortedAssetTypes) {
      const field = assetType.fields?.find(f => f.name === pinnedName);
      if (field && !seenFieldNames.has(field.name)) {
        seenFieldNames.add(field.name);
        columns.push(generateColumnForAssetField(field, undefined, true));
        break;
      }
    }
  });

  sortedAssetTypes.forEach(assetType => {
    const sortedFields = [...(assetType.fields ?? [])].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );

    sortedFields.forEach(field => {
      if (seenFieldNames.has(field.name)) {
        return;
      }
      seenFieldNames.add(field.name);
      columns.push(generateColumnForAssetField(field, undefined, true));
    });
  });

  return columns;
}

function getAllTypesColumns(assetTypes: AssetTypeWithFields[]) {
  const fieldColumns = generateColumnsForAllAssetTypeFields(assetTypes);
  const assetTypeColumnForAllTypes = assetTypeSelectColumn(
    assetTypes.map(assetType => assetType.name)
  );

  // The identifier field differs by type (Name for Turbines, Serial Number for Blades), so the
  // mixed-type table links every row from its own Asset column and shows each field as plain data.
  return [assetColumn, assetTypeColumnForAllTypes, ...fieldColumns];
}

/**
 * Function to fetch all the columns necessary for a given asset type
 * @param assetTypeName - Name of the asset type to get columns for
 * @param assetTypeFieldsData - Collection of field data to generate columns for. Can be undefined in the case of the AllAssets type.
 * @param safeToGenerateFieldData - Indicates that the query fetching the field data has completed with no errors, and it is safe to generate columns with the field data.
 * @param parentAssetId - Optional parent Asset; the Turbine table scopes its Turbine filter to it.
 * @param allAssetTypes - Asset types (with fields) shown by the All Assets table.
 * @param options - Optional columns to append last, see `AssetTableColumnOptions`.
 * @returns An array of Column definitions for the given asset type.
 */
export function getColumnsForAssetType(
  assetTypeName: string,
  assetTypeFieldsData: AtlasGqlGetAssetFieldsForTypeQuery | undefined,
  safeToGenerateFieldData: boolean,
  parentAssetId?: string,
  allAssetTypes: AssetTypeWithFields[] = [],
  options: AssetTableColumnOptions = {}
): TTableColumnDef[] {
  const columns = getFieldColumnsForAssetType(
    assetTypeName,
    assetTypeFieldsData,
    safeToGenerateFieldData,
    parentAssetId,
    allAssetTypes
  );
  // Placeholder columns while the field data loads, or if its query fails. The Inspection columns
  // don't depend on the field data, so they're kept either way.
  const fallbackColumns =
    assetTypeName === allAssetsTypeName
      ? [assetTypeSelectColumn([])]
      : [assetColumn, assetTypeColumn];
  return withInspectionColumns(columns ?? fallbackColumns, options);
}

/** The asset type's columns, or `undefined` while its field data isn't safe to use. */
function getFieldColumnsForAssetType(
  assetTypeName: string,
  assetTypeFieldsData: AtlasGqlGetAssetFieldsForTypeQuery | undefined,
  safeToGenerateFieldData: boolean,
  parentAssetId: string | undefined,
  allAssetTypes: AssetTypeWithFields[]
): TTableColumnDef[] | undefined {
  switch (assetTypeName) {
    case 'Site': {
      if (!safeToGenerateFieldData) {
        break;
      }
      return getSiteColumns();
    }
    case 'Turbine': {
      if (!assetTypeFieldsData || !safeToGenerateFieldData) {
        break;
      }
      const statusOptions = getOptionsForField('Status', assetTypeFieldsData);
      const fields: TTurbineFields = {
        statusOptions: statusOptions,
      };
      return getTurbineColumns(fields, parentAssetId);
    }
    case 'Blade': {
      if (!assetTypeFieldsData || !safeToGenerateFieldData) {
        break;
      }
      const bladePositionOptions = getOptionsForField('Position', assetTypeFieldsData);
      const serialNumberVerifiedOptions = getOptionsForField(
        'Serial Number Verified',
        assetTypeFieldsData
      );
      const assetStatusOptions = getOptionsForField('Status', assetTypeFieldsData);
      const fields: TBladeFields = {
        bladePositionOptions,
        serialNumberVerifiedOptions,
        assetStatusOptions,
      };
      return getBladeColumns(fields);
    }
    case allAssetsTypeName: {
      if (!safeToGenerateFieldData) {
        break;
      }
      return getAllTypesColumns(allAssetTypes);
    }
    default: {
      if (safeToGenerateFieldData && assetTypeFieldsData) {
        const uniqueIdField = generateUniqueIdentifierColumnForAsset(assetTypeFieldsData);
        const fields = generateColumnsForAssetFields(assetTypeFieldsData);
        return [
          uniqueIdField,
          {
            ...filterableSiteColumn,
            dataIndex: 'asset_site',
            sortKey: 'ancestorSiteName',
          },
          componentTurbineColumn,
          ...fields,
        ];
      }
    }
  }
  return undefined;
}
